// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Dices, Eye, EyeOff, Loader2, UserRoundPlus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError } from "@/api/api-error";
import { useCreateUserMutation } from "@/api/hooks/use-users";
import type { components } from "@/api/schema";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { VirtualMultiSelectPopover } from "@/components/shared/virtual-multi-select-popover";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";

const LOCAL_REALM = "local";

/** User-group flag string that flips on the Field Tool's FUE (first-user-experience) training flow. */
const FT_FUE_TRAINING_FLAG: components["schemas"]["UserFlagEnum"] = "ft_fue_training_required";

/** User-group flag that forces the user through a password change on next login. */
const REQUIRE_PASSWORD_RESET_FLAG: components["schemas"]["UserFlagEnum"] = "require_password_reset";

/**
 * The FT FUE training toggle is only relevant for ops users. A group qualifies when "ops" is one of
 * its underscore-delimited segments (e.g. `focal_ops`, `ops_field`) — a plain substring match would
 * wrongly catch names like `dev_opsy`.
 */
function groupsAllowFueTraining(groups: string[]): boolean {
  return groups.some((name) => name.split("_").includes("ops"));
}

function createUserSchema(isAdmin: boolean, t: (key: string) => string) {
  return z
    .object({
      email: z.email(t("users.form.validation.invalidEmail")),
      username: z.string().optional(),
      fullName: z.string().min(1, t("users.form.validation.required")),
      realm: z.string().min(1, t("users.form.validation.required")),
      password: z.string().optional(),
      groups: z.array(z.string()).min(1, t("users.form.validation.required")),
      defaultStoreId: z.string().min(1, t("users.form.validation.required")),
      requirePasswordReset: z.boolean(),
      ftFueTraining: z.boolean(),
    })
    .superRefine((data, ctx) => {
      // A local-realm user always needs a password. Admins bypass the complexity rules (they may
      // set a weak/temporary one) — but never the password itself, so an empty field is still invalid.
      if (data.realm === LOCAL_REALM) {
        if (!data.password) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("users.form.validation.required"),
            path: ["password"],
          });
        } else if (!isAdmin && data.password.length < 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("users.form.validation.passwordMinLength"),
            path: ["password"],
          });
        }
      }
    });
}

type CreateUserValues = z.infer<ReturnType<typeof createUserSchema>>;

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select these groups (e.g. when creating a user from a group's Users tab). */
  defaultGroups?: string[];
}

export function CreateUserDialog({ open, onOpenChange, defaultGroups }: CreateUserDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: context } = useBackofficeContext();
  const isAdmin = user?.groups.includes("admin") ?? false;
  const createUser = useCreateUserMutation();

  const schema = useMemo(() => createUserSchema(isAdmin, t), [isAdmin, t]);

  const form = useForm<CreateUserValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      username: "",
      fullName: "",
      realm: LOCAL_REALM,
      password: "",
      groups: defaultGroups ?? [],
      defaultStoreId: "",
      requirePasswordReset: false,
      ftFueTraining: false,
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const selectedRealm = form.watch("realm");
  const isLocalRealm = selectedRealm === LOCAL_REALM;
  const selectedGroups = form.watch("groups");
  const showFueTraining = useMemo(() => groupsAllowFueTraining(selectedGroups), [selectedGroups]);

  const prevOpen = useRef(false);
  useEffect(
    function resetFormOnDialogOpen() {
      // Reset form only when dialog opens (false → true), not when it closes
      if (open && !prevOpen.current) {
        setFormError(null);
        form.reset({
          email: "",
          username: "",
          fullName: "",
          realm: LOCAL_REALM,
          password: "",
          groups: defaultGroups ?? [],
          defaultStoreId: "",
          requirePasswordReset: false,
          ftFueTraining: false,
        });
      }
      prevOpen.current = open;
    },
    [open, form, defaultGroups],
  );

  // Clear password (and the password-reset flag it implies) when switching away from local realm
  useEffect(
    function clearPasswordOnNonLocalRealm() {
      if (!isLocalRealm) {
        form.setValue("password", "");
        form.clearErrors("password");
        form.setValue("requirePasswordReset", false);
      }
    },
    [isLocalRealm, form],
  );

  // Never submit a stale FUE flag once the group selection no longer qualifies (the field is hidden)
  useEffect(
    function clearFueTrainingWhenHidden() {
      if (!showFueTraining) {
        form.setValue("ftFueTraining", false);
      }
    },
    [showFueTraining, form],
  );

  async function onSubmit(values: CreateUserValues) {
    setFormError(null);
    const flags: components["schemas"]["UserFlagEnum"][] = [];
    if (isLocalRealm && values.requirePasswordReset) flags.push(REQUIRE_PASSWORD_RESET_FLAG);
    // Only stamp the FUE flag when the toggle is actually visible (qualifying ops group selected).
    if (showFueTraining && values.ftFueTraining) flags.push(FT_FUE_TRAINING_FLAG);
    try {
      const result = await createUser.mutateAsync({
        email: values.email,
        userName: values.username || undefined,
        fullName: values.fullName,
        realm: values.realm,
        password: values.password || undefined,
        groups: values.groups,
        defaultStoreId: values.defaultStoreId,
        flags: flags.length ? flags : undefined,
      });
      toast.success(t("users.notifications.created", { name: values.fullName || values.email }));
      onOpenChange(false);
      form.reset();
      if (result?.user?.publicId) {
        navigate({ to: "/iam/users/$publicId", params: { publicId: result.user.publicId } });
      }
    } catch (err) {
      // A duplicate email or username is the expected, actionable failure — anchor it to the field
      // that clashed. The backend raises EntityAlreadyExistsError → HTTP 409 Conflict, with a detail
      // naming the field ("user.email:…" / "user.username:…"). We key on the 409 status, then route by
      // the detail prefix. Anything else surfaces as an inline banner so the dialog never fails silently.
      if (err instanceof ApiError && err.status === 409) {
        const isUsername = /user_?name/i.test(err.detail ?? "");
        form.setError(isUsername ? "username" : "email", {
          message: isUsername ? t("users.form.validation.usernameExists") : t("users.form.validation.emailExists"),
        });
      } else {
        setFormError(err instanceof ApiError ? err.message : t("users.notifications.createFailed"));
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-user"
        className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={UserRoundPlus} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>{t("users.dialog.createTitle")}</DialogTitle>
                </DialogHeader>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("users.form.email")} />
                      <FormControl>
                        <Input type="email" autoComplete="off" data-1p-ignore data-lpignore="true" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("users.form.fullName")} />
                      <FormControl>
                        <Input autoComplete="off" data-1p-ignore data-lpignore="true" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="username"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabelRow
                        label={t("users.form.username")}
                        // Hide the "Not required" hint while an error occupies the message slot, so the
                        // two don't compete on the same row; it reappears once the error clears.
                        aside={
                          fieldState.error ? undefined : (
                            <span className="text-xs leading-none text-muted-foreground">
                              {t("users.form.notRequired")}
                            </span>
                          )
                        }
                      />
                      <FormControl>
                        <Input autoComplete="off" data-1p-ignore data-lpignore="true" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="realm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("users.form.realm")} />
                      <FormControl>
                        <VirtualSelectPopover
                          value={field.value}
                          onChange={field.onChange}
                          options={(context?.userRealms ?? []).map((r) => ({ label: r, value: r }))}
                          placeholder={t("users.form.selectRealm")}
                          searchPlaceholder={t("users.form.searchRealms")}
                          emptyText={t("users.form.noRealms")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {isLocalRealm && (
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow
                          label={t("users.form.password")}
                          aside={
                            isAdmin ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs leading-none text-muted-foreground cursor-help underline decoration-dotted underline-offset-2">
                                    {t("users.form.validationDisabled")}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">{t("users.form.validationDisabledHint")}</TooltipContent>
                              </Tooltip>
                            ) : undefined
                          }
                        />
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              autoComplete="new-password"
                              data-1p-ignore
                              data-lpignore="true"
                              className="pr-16"
                              {...field}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-8 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
                                const array = new Uint32Array(16);
                                crypto.getRandomValues(array);
                                const password = Array.from(array, (v) => chars[v % chars.length]).join("");
                                field.onChange(password);
                                setShowPassword(true);
                              }}
                              tabIndex={-1}
                            >
                              <Dices className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                              onClick={() => setShowPassword((prev) => !prev)}
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="groups"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("users.form.groups")} />
                      <FormControl>
                        <VirtualMultiSelectPopover
                          values={field.value}
                          onChange={field.onChange}
                          options={(context?.userGroups ?? []).map((g) => ({ label: g, value: g }))}
                          placeholder={t("users.form.selectGroups")}
                          searchPlaceholder={t("users.form.searchGroups")}
                          emptyText={t("users.form.noGroups")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="defaultStoreId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("users.form.defaultStore")} />
                      <FormControl>
                        <VirtualSelectPopover
                          value={field.value}
                          onChange={field.onChange}
                          options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                          placeholder={t("users.form.selectStore")}
                          searchPlaceholder={t("users.form.searchStores")}
                          emptyText={t("users.form.noStores")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {/* Divider precedes the flag toggles; skip it entirely when none are shown. */}
                {(isLocalRealm || showFueTraining) && <Separator />}

                {/* Requiring a password reset only makes sense for local-realm (password) users. */}
                {isLocalRealm && (
                  <FormField
                    control={form.control}
                    name="requirePasswordReset"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-3">
                          <FormControl>
                            <Switch
                              id="require-password-reset"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <Label htmlFor="require-password-reset">{t("users.form.requirePasswordReset")}</Label>
                        </div>
                      </FormItem>
                    )}
                  />
                )}

                {showFueTraining && (
                  <FormField
                    control={form.control}
                    name="ftFueTraining"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-3">
                          <FormControl>
                            <Switch id="ft-fue-training" checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <Label htmlFor="ft-fue-training">{t("users.form.ftFueTraining")}</Label>
                        </div>
                      </FormItem>
                    )}
                  />
                )}

                {formError && <p className="text-sm text-destructive">{formError}</p>}
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserRoundPlus className="h-4 w-4" />
                )}
                {t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
